import {
  Dialog,
  type PaletteColorOptions,
  type PaletteOptions,
} from '@mui/material';
import { lighten, darken } from '@mui/material/styles';
import { navigate } from 'gatsby';
import React from 'react';
import { useLocation, useSessionStorage } from 'react-use';

import { IdleTimer } from '@components/IdleTimer';
import DisableEnableUserContent from '@components/Modal/Content/DisableEnableUserModalContent';

import { SESSION_EXPIRED } from '@constants/error-codes';
import * as ROUTES from '@constants/routes';

import { useAccountSettings } from '@hooks/api/account-settings';
import { useApp } from '@hooks/useApp';
import { useCuration } from '@hooks/useCuration';
import { useDocumentViewer } from '@hooks/useDocumentViewer';
import { useMessenger } from '@hooks/useMessenger';
import { useModal } from '@hooks/useModal';
import { usePolling } from '@hooks/usePolling';

import type { Profile } from '@models/session';

import { formatPatientId } from '@utils/format';
import Ethora from '@utils/messenger/ethora';
import { mapProfile, mapProfiles } from '@utils/profiles';
import { hasRole } from '@utils/role';
import { getSortedProfiles } from '@utils/sort/profiles';
import { createQueryParams } from '@utils/url';

import {
  useLogoutMutation,
  useRefreshTokenMutation,
} from 'generated/session.generated';
import { RoleName } from 'generated/types';
import {
  useGetUserRecordLazyQuery,
  useGetUserRecordQuery,
} from 'generated/user.generated';

import { baseThemeOptions } from '../theme';

interface ReactivationDialogState {
  open: boolean;
  email?: string;
}
interface SessionState {
  logged_in_user_id: string | null;
  active_user_id: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoadingAuth: boolean;
  isAdmin: boolean;
  isPractitioner: boolean;
  isClient: boolean;
  isCurator: boolean;
  isCuratorAdmin: boolean;
  isPatient: boolean;
  isAccountAdmin: boolean;
  isUser: boolean;
  isOrgAdmin: boolean;
  reactivationDialog: ReactivationDialogState;
}

export enum SessionActions {
  Reset = 'reset',
  ValidateSession = 'validateSession',
  Login = 'login',
  SetActiveProfile = 'setActiveProfile',
  OpenReactivationDialog = 'openReactivationDialog',
  CloseReactivationDialog = 'closeReactivationDialog',
}

type Payload = {
  [SessionActions.Reset]: undefined;
  [SessionActions.Login]: {
    user: Profile;
    active_user_id: string | null;
    isAdmin: boolean;
    isCurator: boolean;
    isCuratorAdmin: boolean;
    isPractitioner: boolean;
    isClient: boolean;
    isPatient: boolean;
    isAccountAdmin: boolean;
    isUser: boolean;
    isOrgAdmin: boolean;
  };
  [SessionActions.ValidateSession]: undefined;
  [SessionActions.SetActiveProfile]: {
    active_user_id: string;
  };
  [SessionActions.OpenReactivationDialog]: {
    email: string;
  };
  [SessionActions.CloseReactivationDialog]: undefined;
};

const initialSessionState: SessionState = {
  reactivationDialog: {
    open: false,
    email: undefined,
  },
  logged_in_user_id: null,
  active_user_id: null,
  isAuthenticated: false,
  isInitialized: false,
  isLoadingAuth: false,
  isAdmin: false,
  isPractitioner: false,
  isClient: false,
  isCurator: false,
  isCuratorAdmin: false,
  isAccountAdmin: false,
  isPatient: false,
  isUser: false,
  isOrgAdmin: false,
};

function reducer(state: SessionState, action: Action<Payload>): SessionState {
  switch (action.type) {
    case SessionActions.Reset:
      return initialSessionState;
    case SessionActions.ValidateSession:
      return {
        ...state,
        isAuthenticated: true,
        // isLoadingAuth: false,
      };
    case SessionActions.Login:
      return {
        ...state,
        isInitialized: true,
        isAuthenticated: true,
        isAdmin: action.isAdmin,
        isCurator: action.isCurator,
        isCuratorAdmin: action.isCuratorAdmin,
        isPractitioner: action.isPractitioner,
        isClient: action.isClient,
        logged_in_user_id: action.user.user_id,
        active_user_id: action.active_user_id ?? action.user.user_id,
        isAccountAdmin: action.isAccountAdmin,
        isPatient: action.isPatient,
        isUser: action.isUser,
        isOrgAdmin: action.isOrgAdmin,
      };
    case SessionActions.SetActiveProfile:
      return {
        ...state,
        active_user_id: action.active_user_id,
      };

    case SessionActions.OpenReactivationDialog:
      return {
        ...state,
        reactivationDialog: {
          open: true,
          email: action.email,
        },
      };
    case SessionActions.CloseReactivationDialog:
      return {
        ...state,
        reactivationDialog: {
          open: false,
          email: undefined,
        },
      };
    default:
      // throw new Error('Reducer case not found');
      return { ...state };
  }
}

export const SessionContext = React.createContext<{
  state: SessionState;
  dispatch: React.Dispatch<Action<Payload>>;
  initializeSession: (
    user_id: string,
    id: string,
    access: string
  ) => Promise<any>;
  logout: () => Promise<void>;
  clearStorage: () => Promise<void>;
  loggedInProfile: Profile | null;
  activeProfile: Profile | null;
  allProfiles: Profile[];
  isLoadingAuth: boolean;
  openReactivationDialog: (email: string) => void;
  closeReactivationDialog: () => void;
}>({
  state: initialSessionState,
  dispatch: () => null,
  initializeSession: async () => Promise.resolve(),
  logout: async () => Promise.resolve(),
  clearStorage: async () => Promise.resolve(),
  loggedInProfile: null,
  activeProfile: null,
  allProfiles: [],
  isLoadingAuth: false,
  openReactivationDialog: () => {},
  closeReactivationDialog: () => {},
});
SessionContext.displayName = 'SessionContext';

interface SessionProviderProps {
  onInitialize: (idToken: string, accessToken: string) => void;
  onReset: () => void;
}

export const SessionProvider = ({
  children,
  onInitialize,
  onReset,
}: React.PropsWithChildren<SessionProviderProps>) => {
  const { reset: resetApp } = useApp();
  const { reset: resetCuration } = useCuration();
  const { reset: resetMessenger } = useMessenger();
  const { resetModal } = useModal();
  const { reset: resetDocumentViewer } = useDocumentViewer();

  const [state, dispatch] = React.useReducer(reducer, initialSessionState);

  const openReactivationDialog = (email: string) => {
    dispatch({
      type: SessionActions.OpenReactivationDialog,
      email,
    });
  };

  const closeReactivationDialog = () => {
    dispatch({
      type: SessionActions.CloseReactivationDialog,
    });
  };

  const location = useLocation();
  const [sessionActiveUserId, setSessionActiveUserId] = useSessionStorage<
    string | null
  >('session:active_user_id');

  const [refreshToken] = useRefreshTokenMutation();
  const [loggedInUserLoading, setLoggedInUserLoading] = React.useState(false);
  const [getUser, { data: loggedInUserData }] = useGetUserRecordLazyQuery();
  const [signOut] = useLogoutMutation();

  const { active_user_id, isAuthenticated } = state;

  const loggedInProfile = React.useMemo(
    () => mapProfile(loggedInUserData?.getUserRecord),
    [loggedInUserData?.getUserRecord]
  );

  const { data: activeUserData, loading: activeProfileLoading } =
    useGetUserRecordQuery({
      variables: {
        input: { user_id: active_user_id ?? '' },
        include_features: true,
      },
      skip: !active_user_id || !isAuthenticated,
      fetchPolicy: 'cache-first',
    });
  const activeProfile = React.useMemo(
    () => mapProfile(activeUserData?.getUserRecord),
    [activeUserData?.getUserRecord]
  );

  const allProfiles = React.useMemo(() => {
    const loggedInUser = loggedInUserData?.getUserRecord;
    if (loggedInUser) {
      const profiles = [
        ...(state.isCurator ? [] : [loggedInUser]),
        ...loggedInUser.users,
      ];
      return getSortedProfiles(
        mapProfiles(profiles),
        activeProfile?.user_id ?? ''
      );
    }
    return [];
  }, [
    loggedInUserData?.getUserRecord,
    state.isCurator,
    activeProfile?.user_id,
  ]);

  const handleChatLogout = async () => {
    resetMessenger();

    const logoutService = await Ethora.getLogoutService();
    logoutService.performLogout();
  };

  /**
   * Resets the Apollo Client cache and removes all cookies, session storage and local storage.
   * Also dispatches a reset action to the session reducer and closes any globally-managed
   * overlays (modal, document viewer) so they don't remain mounted across logout.
   */
  const clearStorage = async () => {
    onReset();
    // localStorage.clear();
    sessionStorage.clear();
    resetApp();
    await resetCuration();
    resetModal();
    resetDocumentViewer();
    dispatch({ type: SessionActions.Reset });
  };

  /** Used for explicit logout with user interaction */
  const logout = async (redirect?: string) => {
    await handleChatLogout();
    // Reset app state, clear cookies and storage
    await signOut();
    await clearStorage();

    // Direct to login page
    navigate(ROUTES.LOGIN, redirect ? { state: { redirect } } : undefined);
    return Promise.resolve();
  };

  /** Used for implicit logout, no user interaction. E.g. idle expiry */
  const expireSession = async (redirect?: string) => {
    await handleChatLogout();
    // Reset app state, clear cookies and storage
    await clearStorage();

    // Direct to login page with redirect_uri
    const redirect_uri = createQueryParams(ROUTES.LOGIN, {
      // Return to previous page on kick
      redirect_uri: (redirect ?? window.location.pathname) + location.search,
    });
    navigate(redirect_uri, {
      state: { redirect: SESSION_EXPIRED },
    });
    return Promise.resolve();
  };

  const initializeSession = async (
    user_id: string,
    id: string,
    access: string,
    setActiveProfile = false
  ) => {
    onInitialize(id, access);
    sessionStorage.clear();
    //localStorage.clear();

    setLoggedInUserLoading(true);
    const response = await getUser({
      variables: {
        input: {
          user_id,
        },
        include_features: true,
      },
    });
    const user = response.data?.getUserRecord;

    if (user) {
      dispatch({
        type: SessionActions.Login,
        user: {
          ...user,
          account_id: user.account_id,
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          user_id: user.user_id,
          roles: user.roles,
          patient_id: formatPatientId(user.vitall_patient?.patient_id),
          vitall_patient_id: user.vitall_patient?.vitall_patient_id ?? null,
          account: {
            account_id: user.account_id,
            isManaged: Boolean(user.account.parent_id),
          },
        },
        isAdmin: hasRole(user, RoleName.Admin),
        isCurator:
          hasRole(user, RoleName.Curator) ||
          hasRole(user, RoleName.CuratorAdmin),
        isCuratorAdmin: hasRole(user, RoleName.CuratorAdmin),
        isPractitioner:
          hasRole(user, RoleName.Practitioner) ||
          hasRole(user, RoleName.OrgAdmin),
        isClient: hasRole(user, RoleName.Client),
        isAccountAdmin: hasRole(user, RoleName.AccountAdmin),
        isPatient: hasRole(user, RoleName.Patient),
        isUser: hasRole(user, RoleName.User),
        isOrgAdmin: hasRole(user, RoleName.OrgAdmin),
        active_user_id: setActiveProfile ? sessionActiveUserId : null,
      });
    }
    setLoggedInUserLoading(false);
    return response;
  };

  const refreshSession = async (redirect?: string) => {
    const response = await refreshToken();
    if (!response.errors && response.data?.refreshToken.id) {
      const { id, access, user_id } = response.data.refreshToken;
      try {
        initializeSession(user_id, id, access, true);
      } catch {
        expireSession(redirect);
      }
    } else {
      expireSession(redirect);
    }
  };

  const checkSession = async () => {
    if (!state.isAuthenticated) {
      return;
    }
    const response = await refreshToken();
    if (response.errors?.length || !response.data?.refreshToken.id) {
      await expireSession();
    } else {
      dispatch({ type: SessionActions.ValidateSession });
    }
  };

  React.useEffect(() => {
    function trim(s: string, c: string) {
      if (c === ']') c = '\\]';
      if (c === '^') c = '\\^';
      if (c === '\\') c = '\\\\';
      return s.replace(new RegExp('^[' + c + ']+|[' + c + ']+$', 'g'), '');
    }

    const { pathname = '' } = location;
    const trimmedPathname =
      pathname.length > 1 ? trim(pathname, '/') : pathname;
    let isUnauthedRoute: boolean = false;
    if (
      pathname === ROUTES.EMERGENCY_ACCESS ||
      pathname === '/emergencyaccess/'
    ) {
      return;
    }

    for (const route of ROUTES.UNAUTHENTICATED_ROUTES) {
      let checkRoute = route;
      if (checkRoute.startsWith('/')) {
        checkRoute = checkRoute.slice(1);
      }
      if (checkRoute.endsWith('/')) {
        checkRoute = checkRoute.slice(0, checkRoute.length - 1);
      }
      if (trimmedPathname) {
        const re = new RegExp(trimmedPathname, 'u');
        if (re.test(checkRoute)) {
          isUnauthedRoute = true;
          break;
        }
      }
    }
    if (process.env.NODE_ENV === 'test' || isUnauthedRoute) {
      return;
    }
    refreshSession(pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const { active_user_id } = state;
    setSessionActiveUserId(active_user_id);
    resetApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.active_user_id]);

  usePolling(checkSession);

  const isLoadingAuth = loggedInUserLoading || activeProfileLoading;

  const value = React.useMemo(
    () => ({
      state,
      dispatch,
      initializeSession,
      logout,
      clearStorage,
      activeProfile,
      loggedInProfile,
      allProfiles,
      isLoadingAuth,
      openReactivationDialog,
      closeReactivationDialog,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeProfile, allProfiles, loggedInProfile, isLoadingAuth, state]
  );

  const { setCustomPalette } = useApp();
  const accountId = loggedInProfile?.account_id ?? '';
  const { accountSettings: accountData } = useAccountSettings(accountId);

  function getMainColor(color?: PaletteColorOptions | string | null): string {
    if (!color) return '';
    if (typeof color === 'string') return color;
    if ('main' in color && typeof color.main === 'string') return color.main;
    return '';
  }

  React.useEffect(() => {
    const primaryColor =
      accountData?.getAccountSetting?.branding_color_primary ||
      getMainColor(baseThemeOptions.palette?.primary);

    const secondaryColor =
      accountData?.getAccountSetting?.branding_color_secondary ||
      getMainColor(baseThemeOptions.palette?.secondary);

    setCustomPalette({
      primary: {
        main: primaryColor,
        light: lighten(primaryColor, 0.15),
        dark: darken(primaryColor, 0.15),
      },
      secondary: {
        main: secondaryColor,
        light: lighten(secondaryColor, 0.15),
      },
    } as PaletteOptions);
  }, [
    accountData?.getAccountSetting?.branding_color_primary,
    accountData?.getAccountSetting?.branding_color_secondary,
    setCustomPalette,
  ]);
  return (
    <SessionContext.Provider value={value}>
      <IdleTimer onIdle={expireSession} disabled={!state.isAuthenticated} />
      {children}
      <Dialog
        open={state.reactivationDialog.open}
        onClose={closeReactivationDialog}
      >
        <DisableEnableUserContent
          disabled
          email={state.reactivationDialog.email ?? ''}
          onSubmit={closeReactivationDialog}
        />
      </Dialog>
    </SessionContext.Provider>
  );
};
